import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });
export const getToken = () => localStorage.getItem("token");
client.interceptors.request.use((c) => {
  const t = getToken();
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
client.interceptors.response.use((r) => r, (err) => {
  if (err?.response?.status === 401 && !String(err.config?.url || "").startsWith("/auth/")) {
    localStorage.removeItem("token");
    if (window.location.pathname !== "/login") window.location.href = "/login";
  }
  return Promise.reject(err);
});
export const http = client;

const crud = (base) => ({
  list: (params) => client.get(base, { params }).then((r) => r.data),
  create: (d) => client.post(base, d).then((r) => r.data),
  update: (id, d) => client.put(`${base}/${id}`, d).then((r) => r.data),
  remove: (id, params) => client.delete(`${base}/${id}`, { params }).then((r) => r.data),
});
export const saas = {
  customers: crud("/customers"),
  jobs: crud("/jobs"),
  quotes: crud("/quotes"),
  invoices: crud("/invoices"),
  employees: crud("/employees"),
  dashboard: () => client.get("/dashboard").then((r) => r.data),
  fromLead: (id) => client.post(`/customers/from-lead/${id}`).then((r) => r.data),
  jobStatus: (id, d) => client.post(`/jobs/${id}/status`, d).then((r) => r.data),
  acceptQuote: (id) => client.post(`/quotes/${id}/accept`).then((r) => r.data),
  invoiceFromJobs: (d) => client.post("/invoices/from-jobs", d).then((r) => r.data),
  checklists: () => client.get("/checklists").then((r) => r.data),
  company: () => client.get("/company").then((r) => r.data),
  updateCompany: (d) => client.put("/company", d).then((r) => r.data),
};
export const authApi = {
  login: (d) => client.post("/auth/login", d).then((r) => r.data),
  register: (d) => client.post("/auth/register", d).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
};

export const api = {
  geocode: (q) => client.get("/geocode", { params: { q } }).then((r) => r.data),
  generate: (payload) => client.post("/leads/generate", payload).then((r) => r.data),
  listLeads: (params) => client.get("/leads", { params }).then((r) => r.data),
  stats: () => client.get("/stats").then((r) => r.data),
  setStatus: (id, status) => client.post(`/leads/${id}/status`, { status }).then((r) => r.data),
  bulk: (ids, status) => client.post("/leads/bulk", { ids, status }).then((r) => r.data),
  updateEmail: (id, payload) => client.put(`/leads/${id}/email`, payload).then((r) => r.data),
  clear: () => client.delete("/leads").then((r) => r.data),
  send: (ids) => client.post("/send", { ids }).then((r) => r.data),
  gmailStatus: () => client.get("/gmail/status").then((r) => r.data),
  gmailLogin: () => client.get("/gmail/login").then((r) => r.data),
  gmailDisconnect: () => client.post("/gmail/disconnect").then((r) => r.data),
  connectSmtp: (password, email) => client.post("/gmail/connect-smtp", { app_password: password, email }).then((r) => r.data),
  listSaved: () => client.get("/saved-searches").then((r) => r.data),
  createSaved: (payload) => client.post("/saved-searches", payload).then((r) => r.data),
  deleteSaved: (id) => client.delete(`/saved-searches/${id}`).then((r) => r.data),
  getSettings: () => client.get("/settings").then((r) => r.data),
  updateSettings: (payload) => client.put("/settings", payload).then((r) => r.data),
  applySettings: () => client.post("/settings/apply-to-leads").then((r) => r.data),
  syncSent: () => client.post("/gmail/sync-sent").then((r) => r.data),
  syncStatus: () => client.get("/gmail/sync-status").then((r) => r.data),
};

export const exportUrl = (status = "godkjent") => `${API}/leads/export?status=${encodeURIComponent(status)}&token=${getToken()}`;

export const CATEGORY_GROUPS = [
  { group: "Mat & drikke", items: ["Restaurant", "Kafé", "Pub / Bar", "Bakeri", "Catering", "Kiosk"] },
  { group: "Skjønnhet & velvære", items: ["Frisør", "Salong", "Spa / velvære"] },
  { group: "Helse", items: ["Klinikk", "Tannlege", "Legekontor", "Fysioterapi", "Apotek", "Optiker", "Veterinær"] },
  { group: "Kontor & tjenester", items: ["Kontor", "Kontorbygg", "Advokat", "Regnskapsfører", "Eiendomsmegler", "Eiendomsforvalter"] },
  { group: "Utdanning & omsorg", items: ["Barnehage", "Skole", "Sykehjem", "Bibliotek"] },
  { group: "Bolig & overnatting", items: ["Sameie", "Borettslag", "Airbnb / utleie", "Hotell / overnatting", "Konferansesenter", "Campingplass"] },
  { group: "Trening & fritid", items: ["Treningssenter", "Svømmehall", "Idrettslag", "Kino / teater", "Museum / galleri", "Nattklubb"] },
  { group: "Butikk & handel", items: ["Butikk", "Dagligvare", "Kjøpesenter", "Møbelbutikk", "Elektronikkbutikk", "Byggevare", "Blomsterbutikk"] },
  { group: "Bil & transport", items: ["Bensinstasjon", "Bil / verksted", "Lager / logistikk"] },
  { group: "Industri & håndverk", items: ["Industribygg", "Byggefirma", "Rørlegger", "Elektriker", "Maler", "Renseri"] },
  { group: "Annet", items: ["Annet"] },
];

export const CATEGORIES = CATEGORY_GROUPS.flatMap((g) => g.items);

export const RADII = ["Alle", "5 km", "10 km", "15 km", "20 km", "25 km"];

export const AREAS = ["Alle", "Asker", "Bærum", "Nesbru", "Heggedal", "Dikemark", "Slemmestad", "Røyken", "Sandvika", "Slependen", "Vollen", "Drammen", "Lier", "Oslo"];
export const AREA_OPTIONS = ["Asker", "Bærum", "Nesbru", "Heggedal", "Dikemark", "Slemmestad", "Røyken", "Sandvika", "Slependen", "Vollen", "Fornebu", "Lysaker", "Drammen", "Lier", "Oslo"];
export const DISTANCES = ["Alle", "0-5 km", "5-10 km", "10-20 km", "20+ km"];
export const RELEVANCES = ["Alle", "Høy", "Middels", "Lav"];
